/**
 * The task inbox.
 *
 * Tasks a role holds, or a role's queue where nobody has claimed one yet.
 * Claiming and releasing go through the same write handlers the server rendered
 * tier posts to, so the claim lands with the same audit context and the same
 * four-eyes constraints apply.
 *
 * Overdue first, then what falls due soonest, which is the order the server
 * sorted them in. Re-sorting here would quietly disagree with the figures in
 * the counts above the table.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { Dict, Grant, SectionMeta } from '../api/types'
import { useSession } from '../app/session'
import { Badge, Empty, Note, Tile, Tiles, toneOf } from '../components/bits'
import { PageHead } from '../components/PageHead'
import { Panel } from '../components/Panel'

export function Inbox({ data, section, grant }: {
  data: { tasks: Dict[]; counts: Dict }
  section: SectionMeta
  grant?: Grant
}) {
  const { n, dt, lbl, nm, boot } = useSession()
  const navigate = useNavigate()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const tasks = data.tasks || []
  const counts = data.counts || {}

  const act = async (taskId: string, what: 'claim' | 'release') => {
    setBusy(taskId)
    setError(null)
    try {
      await api.act(`/app/tasks/${taskId}/${what}`, {})
      // The list is rebuilt from the server rather than patched: claiming a
      // task may change its state, its assignee and whether it still belongs in
      // this queue at all, and guessing which would be a second rule.
      navigate(0)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHead section={section} grant={grant} />

      <Tiles>
        <Tile label="Open with you" value={n(counts.total)} />
        <Tile label="Overdue" value={n(counts.overdue)}
              tone={Number(counts.overdue) > 0 ? 'bad' : 'good'}
              sub="past the service level on its workflow" />
        <Tile label="Unclaimed in your queue" value={n(counts.unclaimed)}
              tone={Number(counts.unclaimed) > 0 ? 'warn' : 'muted'}
              sub="assigned to your role, not yet to a person" />
      </Tiles>

      {error && <Note kind="bad"><b>Not done.</b> {error}</Note>}

      <Panel title={`Tasks · ${tasks.length}`} explain={section.section_key} tight>
        {tasks.length === 0 ? (
          <Empty title="Nothing is waiting for you">
            Work reaches this inbox from a workflow transition, not from somebody
            remembering to assign it.
          </Empty>
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Reference</th><th>Task</th><th>Type</th><th>State</th>
                  <th>Due</th><th className="num">Hours left</th>
                  <th>Claimed by</th><th className="acts" />
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => {
                  const id = t.task_id as string
                  const overdue = Boolean(t.overdue)
                  const left = t.hours_left as number | null
                  return (
                    <tr key={id}>
                      <td data-label="Reference">
                        <span className="mono clip">{t.task_ref as string}</span>
                      </td>
                      <td data-label="Task">{nm(t, 'title')}</td>
                      <td data-label="Type">
                        <Badge tone="muted">{lbl(t.task_type)}</Badge>
                      </td>
                      <td data-label="State">
                        <Badge tone={toneOf(t.state)}>{lbl(t.state)}</Badge>
                      </td>
                      <td data-label="Due">{t.due_at ? dt(t.due_at) : '—'}</td>
                      <td data-label="Hours left" className="num">
                        {left === null || left === undefined ? '—' : (
                          <Badge tone={overdue ? 'bad' : left < 4 ? 'warn' : 'muted'}>
                            {overdue ? `${n(Math.abs(left), 1)} over` : n(left, 1)}
                          </Badge>
                        )}
                      </td>
                      <td data-label="Claimed by">
                        {(t.claimed_by as string) || <span className="muted">—</span>}
                      </td>
                      <td className="acts">
                        {t.assigned_user
                          ? (t.assigned_user === boot.principal.user_id && (
                              <button className="btn sm" type="button"
                                      disabled={busy === id}
                                      onClick={() => void act(id, 'release')}>
                                Release
                              </button>
                            ))
                          : (
                            <button className="btn sm primary" type="button"
                                    disabled={busy === id}
                                    onClick={() => void act(id, 'claim')}>
                              Claim
                            </button>
                          )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Why a task rather than a notification" explain={section.section_key}>
        <p className="muted">
          A notification is read and forgotten. A task has a state, an owner, a
          service level and a workflow transition that created it, so work that
          stalls is visible as a number rather than as somebody's recollection.
          Escalation is a transition on the workflow binding, which a partner may
          narrow but not invent.
        </p>
      </Panel>
    </>
  )
}
